import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    CircularProgress,
    TextField,
    alpha,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';

export interface ProfilePhotosSectionProps {
    detectedRosterUrl: string;
    photoRefreshUrl: string;
    onPhotoRefreshUrlChange: (url: string) => void;
    refreshingPhotos: boolean;
    onRefreshCollegePhotos: () => void;
}

export function ProfilePhotosSection({
    detectedRosterUrl,
    photoRefreshUrl,
    onPhotoRefreshUrlChange,
    refreshingPhotos,
    onRefreshCollegePhotos,
}: ProfilePhotosSectionProps) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
                p: 1.5,
                background: alpha(theme.palette.primary.main, 0.02),
                order: { xs: 3, md: 3 },
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    letterSpacing: 1,
                    fontSize: '0.85rem',
                }}
            >
                Profile Photos
            </Typography>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhotoCameraIcon sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Import/Refresh College Profile Photos
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {detectedRosterUrl ? (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Re-scrape the college roster page to update profile photos for
                                matched players.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={photoRefreshUrl || detectedRosterUrl}
                                    onChange={(e) => onPhotoRefreshUrlChange(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') onRefreshCollegePhotos();
                                    }}
                                    disabled={refreshingPhotos}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onRefreshCollegePhotos}
                                    disabled={refreshingPhotos}
                                    startIcon={
                                        refreshingPhotos ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <PhotoCameraIcon />
                                        )
                                    }
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Enter the college roster page URL to import profile photos.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="https://thesundevils.com/sports/baseball/roster"
                                    value={photoRefreshUrl}
                                    onChange={(e) => onPhotoRefreshUrlChange(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') onRefreshCollegePhotos();
                                    }}
                                    disabled={refreshingPhotos}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onRefreshCollegePhotos}
                                    disabled={refreshingPhotos || !photoRefreshUrl.trim()}
                                    startIcon={
                                        refreshingPhotos ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <PhotoCameraIcon />
                                        )
                                    }
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Import
                                </Button>
                            </Box>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
